import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaCheckCircle, FaUsers, FaSchool, FaLock, FaLayerGroup, FaChevronRight } from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { TeacherAvatar, TagBadges, formatCount } from "../../components/marketplace";

// A teacher's "channel" PROFILE page — identity, what they teach, subscriber
// count, and the subscribe action. The actual content lives on a separate page
// (/marketplace/teacher/:id/content) so the two are clean, distinct screens.

const TeacherChannel = () => {
    const navigate = useNavigate();
    const { teacherId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await authFetch(`/api/marketplace/teachers/${teacherId}`);
            if (res.status === 404) { setNotFound(true); return; }
            const d = await res.json().catch(() => ({}));
            setData(d.data || null);
        } catch (err) {
            toast.error("Could not load this channel");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teacherId]);

    const handleSubscribe = async () => {
        setSubscribing(true);
        try {
            const res = await authFetch(`/api/marketplace/subscribe/${teacherId}`, { method: "POST" });
            if (res.status === 401 || res.status === 403) throw new Error("Your session expired. Please log in again.");
            const d = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(d.message || "Failed to start subscription");
            window.location.href = d.payment_url;
        } catch (err) {
            toast.error(err.message);
            setSubscribing(false);
        }
    };

    if (loading) return <Shell onBack={() => navigate("/marketplace")}><p className="text-gray-500 p-6">Loading…</p></Shell>;
    if (notFound || !data) {
        return (
            <Shell onBack={() => navigate("/marketplace")}>
                <div className="text-center py-24">
                    <h2 className="text-lg font-semibold text-gray-800">Channel not found</h2>
                    <p className="text-sm text-gray-500 mt-1">This teacher may no longer be available.</p>
                    <button onClick={() => navigate("/marketplace")} className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold">Back to marketplace</button>
                </div>
            </Shell>
        );
    }

    const { teacher, price, subscribed, subscriberCount, contentCount = 0, canSubscribe } = data;
    const goContent = () => navigate(`/marketplace/teacher/${teacherId}/content`);

    return (
        <Shell onBack={() => navigate("/marketplace")} title={teacher.name}>
            {/* Banner */}
            <div className="h-28 sm:h-40 bg-gradient-to-r from-blue-700 via-blue-600 to-blue-400" />

            <div className="max-w-4xl mx-auto px-4 sm:px-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-10 sm:-mt-12">
                    <div className="ring-4 ring-white rounded-full w-fit">
                        <TeacherAvatar name={teacher.name} src={teacher.profile_image} size={96} />
                    </div>
                    <div className="flex-1 sm:pb-2">
                        <h1 className="text-2xl font-bold text-gray-900">{teacher.name}</h1>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
                            {teacher.school && <span className="flex items-center gap-1.5"><FaSchool className="text-gray-400" /> {teacher.school}</span>}
                            <span className="flex items-center gap-1.5"><FaUsers className="text-gray-400" /> {formatCount(subscriberCount)} subscriber{subscriberCount === 1 ? "" : "s"}</span>
                            <span className="flex items-center gap-1.5"><FaLayerGroup className="text-gray-400" /> {contentCount} item{contentCount === 1 ? "" : "s"}</span>
                        </div>
                        <TagBadges subjects={teacher.subjects} classes={teacher.classes} className="mt-2" />
                    </div>
                    <div className="sm:pb-2">
                        {subscribed ? (
                            <span className="inline-flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-5 py-2.5 rounded-full font-semibold text-sm">
                                <FaCheckCircle /> Subscribed
                            </span>
                        ) : (
                            <button onClick={handleSubscribe} disabled={subscribing || !canSubscribe}
                                className="bg-blue-600 text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                                {subscribing ? "Redirecting…" : canSubscribe ? `Subscribe${price != null ? ` · ₦${Number(price).toLocaleString()}/mo` : ""}` : "Not available"}
                            </button>
                        )}
                    </div>
                </div>

                {!subscribed && (
                    <div className="mt-5 bg-blue-50 border border-blue-100 text-blue-800 text-sm rounded-lg p-3 flex items-center gap-2">
                        <FaLock className="shrink-0" /> Subscribe to unlock all of {teacher.name.split(" ")[0]}'s content. It renews monthly and you can cancel anytime.
                    </div>
                )}

                {/* View content CTA */}
                <button onClick={goContent}
                    className="mt-6 mb-10 w-full bg-white rounded-xl shadow-sm hover:shadow-md transition p-5 flex items-center justify-between text-left">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-50 text-blue-600 p-3 rounded-lg"><FaLayerGroup /></div>
                        <div>
                            <p className="font-semibold text-gray-800">View content</p>
                            <p className="text-xs text-gray-400">{contentCount} item{contentCount === 1 ? "" : "s"}, organised by subject &amp; topic</p>
                        </div>
                    </div>
                    <FaChevronRight className="text-gray-400" />
                </button>
            </div>
        </Shell>
    );
};

const Shell = ({ children, onBack, title }) => (
    <div className="min-h-screen bg-gray-50 font-montserrat">
        <nav className="bg-[#193FAE] shadow-md px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 z-20">
            <button onClick={onBack} className="text-white" aria-label="Back"><FaArrowLeft size={18} /></button>
            <h1 className="text-lg font-bold text-white truncate">{title || "Channel"}</h1>
        </nav>
        {children}
    </div>
);

export default TeacherChannel;
